
import { TypedVue } from "@/store/types";
import { Component, Prop, Watch } from "vue-property-decorator";
import { Project, Assessment, Benchmark, ProjectReportType, ProjectReport } from "@/store/internal/internalTypes";
import { TRAITS, TAG_COLOURS } from "@/lib/consts";
import ComparisonRadarChartComponent, { ComparisonAssessmentItem, ComparisonBenchmarkItem, ComparisonChartPreset } from "@/components/admin/ComparisonComponents/ComparisonRadarChartComponent.vue";
import { AssessmentState, CreateProjectReport, CreateReport, SchemasCandidateAssessment, SchemasV2InternalAssessmentsScoring } from "@/lib/serviceTypes";
import { Benchmark as AdminSchemaBenchmark, TeamEffectivenessReportData } from "@/store/admin/adminTypes";
import { ColorMap } from "@/lib/charts/HighChartThemes";
import Vue from "vue";
import { Action, Getter } from "vuex-class";
import { Actions } from "@/store/internal/interalActions";
import { systemAPI } from "@/lib/s3PlatformApi";

import TagPicker from "@/components/input/TagPicker.vue";
import ComparisonColourPickerModal from "./ComparisonComponents/ComparisonColourPickerModal.vue";
import MarkdownEditor from "../input/MarkdownEditor.vue";
import { Getters } from "@/store/admin/adminGetters";
import { Roles } from "@/lib/auth/User";

@Component({
  components: {
    ComparisonRadarChartComponent,
    ComparisonColourPickerModal,
    MarkdownEditor,
    TagPicker,
  },
})
export default class NewProjectReport extends TypedVue {
  @Prop()
  public project!: Project;

  @Prop()
  public assessments!: Assessment[];

  @Prop()
  public benchmarks!: Benchmark[];

  @Prop({ default: null })
  public initialReport!: {
    identifier: string;
    name: string;
    description: string;
    version: string;
    type: ProjectReportType;
    scoringVersion: string;
    assessmentIds: string[];
    data: TeamEffectivenessReportData;
  } | null;

  @Prop({ default: false })
  clientView!: boolean;

  @Prop({ default: () => [] })
  public tagOptions!: string[];

  @Action(Actions.SAVE_PROJECT_REPORT, { namespace: "internal" })
  public saveProjectReport!: (reportArgs: { projectId: string; projectReport: CreateReport }) => Promise<string>;

  @Action(Actions.SAVE_PROJECT_REPORT, { namespace: "admin" })
  public systemSaveProjectReport!: (reportArgs: { projectId: string; projectReport: CreateProjectReport }) => Promise<string>;

  @Action(Actions.UPDATE_REPORT, { namespace: "internal" })
  public updateProjectReport!: (data: { reportId: string; name: string; description: string | undefined; assessmentIds: string[]; data: Record<string, unknown> }) => Promise<void>;

  @Action(Actions.DELETE_REPORT, { namespace: "internal" })
  deleteReport!: (report_id: string) => Promise<ProjectReport>;

  @Getter(Getters.GET_CURRENT_PROJECT_ROOT_BENCHMARK_ID, { namespace: "admin" })
  public rootBenchmarkId!: string;

  @Watch("tags", { deep: true })
  onTagsChange(newVal: Record<string, string[]>) {
    if (this.settingInitialValues) return;
    for (const tagList of Object.values(newVal)) {
      for (const tag of tagList) {
        if (!this.tagColours[tag]) {
          this.tagColours[tag] = TAG_COLOURS[Object.keys(this.tagColours).length % TAG_COLOURS.length];
          this.showColourPicker(tag);
        }
      }
    }
  }

  get enableFourCompetencyGrouping() {
    return this.project?.features?.includes("four_competency_groups") ?? false;
  }

  private mapAssessmentToSchemaAssessment(assessment: Assessment, score: SchemasV2InternalAssessmentsScoring): SchemasCandidateAssessment {
    return {
      assessmentId: assessment.identifier,
      candidateName: assessment.candidate.name,
      isComplete: assessment.state === AssessmentState.ScoringComplete,
      project: assessment.projectId,
      organization: assessment.company,
      competencyScores: score.competencyScores,
      competencies: score.competencies,
      traitScores: score.traitScores,
      readinessScore: score.readiness,
      dateCompleted: assessment.dateCompleted,
    }; // TODO: We need to migrate the comparison radar chart to use the new schema and we can remove this
  }

  private mapBenchmarkToSchemaBenchmark(benchmark: Benchmark): AdminSchemaBenchmark {
    return {
      alwaysShow: benchmark.default,
      benchmarkId: benchmark.identifier,
      colour: benchmark.colour,
      name: benchmark.name,
      competencyScores: benchmark.competencyScores,
      isAdmin: benchmark.isAdmin,
      organization: benchmark.orgId,
      traitScores: benchmark.traitScores,
      traitRanges: benchmark.traitRanges,
      dateCreated: "",
      lastUpdated: "",
    }; // TODO: We need to migrate the comparison radar chart to use the new schema and we can remove this
  }

  get assesmentDisplayList() {
    return this.assessments
      .filter((assessment) => assessment.report)
      .filter((assessment) => assessment.scores?.find((x) => x.scoringVersion === this.scoringVerison))
      .map((assessment) => ({
        id: assessment.identifier,
        name: `${!this.clientView ? assessment.identifier.slice(0, 4) + " - " : ""} ${assessment.candidate.name}`,
      }));
  }

  public reportName = "";
  public reportDescription = "";
  public reportVersion = "1.0";
  public scoringVerison = "v2";
  public reportType: ProjectReportType = "team-effectiveness";
  // public selectedAssessmentIds: string[] = [];
  public assessmentSelectedState: Record<string, boolean> = {};
  public participantTitles: { [key: string]: string } = {};
  public traitPriorities: { [trait: string]: number } = {};
  traitWeights: { [trait: string]: number } = {};
  public traits = TRAITS;
  public tags: Record<string, string[]> = {};
  public tagColours: Record<string, string> = {};

  public settingInitialValues = false;
  public showColourPickerModal = false;
  public selectedColourItem: string | null = null;
  public allAssessmentsSelected = false;
  public reportOverviewCustomText = "";
  public teamAlignmentMustHaveCustomText = "";
  public teamAlignmentAllCustomText = "";
  public teamMatrixMustHaveCustomText = "";
  public teamMatrixTopPriorityCustomText = "";
  public executiveSummaries: Record<string, string> = {};
  public generatedReportId: null | string = null;
  public demoMode = false;

  public compariableBenchmarks = Vue.observable<ComparisonBenchmarkItem[]>([]);
  public compariableAssessments = Vue.observable<ComparisonAssessmentItem[]>([]);
  public comparisonPresets = Vue.observable<ComparisonChartPreset[]>([]);

  public editMode = false;
  public traitRankChoices = [
    { value: 0, text: "Neutral" },
    { value: 1, text: "Nice to Have" },
    { value: 2, text: "Must Have" },
  ];
  public weightChoices = [
    { value: 1, text: "Least Important" },
    { value: 2, text: "Less Important" },
    { value: 3, text: "Equally Important" },
    { value: 4, text: "More Important" },
    { value: 5, text: "Most Important" },
  ];
  public titleChoices = [
    { value: "candidate", text: "Participant" },
    // { value: "team", text: "Team" },
    { value: "manager", text: "Manager" },
  ];

  get selectedAssessmentIds() {
    return Object.keys(this.assessmentSelectedState).filter((key) => this.assessmentSelectedState[key]);
  }

  set selectedAssessmentIds(value: string[]) {
    this.assessmentSelectedState = this.assessments.reduce((acc, assessment) => {
      acc[assessment.identifier] = value.includes(assessment.identifier);
      return acc;
    }, {} as Record<string, boolean>);

    this.participantTitles = this.assessments.reduce((acc, assessment) => {
      if ((this.initialReport?.data.managers || []).includes(assessment.identifier)) {
        acc[assessment.identifier] = "manager";
      } else {
        acc[assessment.identifier] = "candidate";
      }
      return acc;
    }, {} as Record<string, string>);
  }

  get selectedCandidates() {
    return this.selectedAssessmentIds.filter((id) => this.participantTitles[id] === "candidate");
  }

  get allTags() {
    return Array.from(new Set(Object.values(this.tags).flat().concat(this.tagOptions)));
  }

  get userIsGlobalAdmin(): boolean {
    const user = this.$auth.user;
    if (user) {
      return user.hasRole(Roles.GLOBAL_ADMIN);
    }
    return false;
  }

  public async saveReport() {
    const traitWeights = Object.entries(this.traitWeights).reduce((acc, [trait, weight]) => {
      acc[trait] = +weight;
      return acc;
    }, {} as Record<string, number>);

    if (this.editMode) {
      await this.updateProjectReport({
        reportId: this.initialReport?.identifier || "",
        name: this.reportName,
        description: this.reportDescription,
        assessmentIds: this.selectedAssessmentIds,
        data: {
          candidates: Object.entries(this.participantTitles)
            .filter(([key, value]) => this.selectedAssessmentIds.includes(key) && value === "candidate")
            .map((x) => x[0]),
          managers: Object.entries(this.participantTitles)
            .filter(([key, value]) => this.selectedAssessmentIds.includes(key) && value === "manager")
            .map((x) => x[0]),
          traitPriorities: this.traitPriorities,
          traitWeights: traitWeights,
          tags: Object.keys(this.tags).reduce((acc, assessment) => {
            if (this.selectedAssessmentIds.includes(assessment)) {
              acc[assessment] = this.tags[assessment];
            }
            return acc;
          }, {} as Record<string, string[]>),
          tagColours: this.tagColours,
          reportOverviewCustomText: this.reportOverviewCustomText,
          teamAlignmentMustHaveCustomText: this.teamAlignmentMustHaveCustomText,
          teamAlignmentAllCustomText: this.teamAlignmentAllCustomText,
          teamMatrixMustHaveCustomText: this.teamMatrixMustHaveCustomText,
          teamMatrixTopPriorityCustomText: this.teamMatrixTopPriorityCustomText,
          comparisonPresets: this.comparisonPresets,
          executiveSummaries: this.executiveSummaries,
          demoMode: this.demoMode,
        },
      });
      this.generatedReportId = this.initialReport?.identifier || "";
    } else {
      const projectReport = {
        name: this.reportName,
        description: this.reportDescription,
        reportVersion: this.reportVersion,
        reportType: this.reportType,
        scoringVersion: this.scoringVerison,
        assessmentIds: this.selectedAssessmentIds,
        data: {
          candidates: Object.entries(this.participantTitles)
            .filter(([key, value]) => this.selectedAssessmentIds.includes(key) && value === "candidate")
            .map((x) => x[0]),
          managers: Object.entries(this.participantTitles)
            .filter(([key, value]) => this.selectedAssessmentIds.includes(key) && value === "manager")
            .map((x) => x[0]),
          traitPriorities: this.traitPriorities,
          traitWeights: traitWeights,
          tags: Object.keys(this.tags).reduce((acc, assessment) => {
            if (this.selectedAssessmentIds.includes(assessment)) {
              acc[assessment] = this.tags[assessment];
            }
            return acc;
          }, {} as Record<string, string[]>),
          tagColours: this.tagColours,
          reportOverviewCustomText: this.reportOverviewCustomText,
          teamAlignmentMustHaveCustomText: this.teamAlignmentMustHaveCustomText,
          teamAlignmentAllCustomText: this.teamAlignmentAllCustomText,
          teamMatrixMustHaveCustomText: this.teamMatrixMustHaveCustomText,
          teamMatrixTopPriorityCustomText: this.teamMatrixTopPriorityCustomText,
          comparisonPresets: this.comparisonPresets,
          executiveSummaries: this.executiveSummaries,
          demoMode: this.demoMode,
        },
      };
      if (this.clientView) {
        this.generatedReportId = await this.systemSaveProjectReport({
          projectId: this.project.identifier,
          projectReport,
        });
      } else {
        this.generatedReportId = await this.saveProjectReport({
          projectId: this.project.identifier,
          projectReport,
        });
      }
    }
  }

  public async deleteProjectReport() {
    if (confirm("Are you sure you want to delete this report?")) {
      await this.deleteReport(this.initialReport?.identifier || "");
      await this.$router.push("/admin/summary");
    }
  }

  public removePreset(preset: ComparisonChartPreset) {
    const index = this.comparisonPresets.findIndex((x) => x.id === preset.id);
    if (index !== -1) {
      this.comparisonPresets.splice(index, 1);
    }
  }

  public savePreset(preset: ComparisonChartPreset) {
    this.comparisonPresets.push(preset);
  }

  public updatePreset(preset: ComparisonChartPreset) {
    const index = this.comparisonPresets.findIndex((x) => x.id === preset.id);
    if (index !== -1) {
      this.comparisonPresets.splice(index, 1, preset);
    }

    if (preset.id.startsWith("insight-preset-")) {
      const assessmentId = preset.id.replace("insight-preset-", "");
      this.executiveSummaries[assessmentId] = preset.description;
    }
  }

  public toggleAll() {
    this.selectedAssessmentIds = this.selectedAssessmentIds.length !== this.assesmentDisplayList.length ? this.assesmentDisplayList.slice().map((x) => x.id) : [];
  }

  public setComparableAssessments() {
    this.compariableAssessments = this.assessments
      .filter((assessment) => this.selectedAssessmentIds.includes(assessment.identifier))
      .filter((assessment) => assessment.scores?.find((x) => x.scoringVersion === "v2"))
      .map((assessment) => ({ assessment, scoring: assessment.scores?.find((x) => x.scoringVersion === "v2") as SchemasV2InternalAssessmentsScoring }))
      .map(({ assessment, scoring }) => ({
        id: assessment.identifier,
        name: `${assessment.identifier.substring(0, 4)} - ${assessment.candidate.name}`,
        hidden: false,
        isLocked: false,
        selected: false,
        color: ColorMap[this.project.theme][scoring.readiness],
        initialColor: ColorMap[this.project.theme][scoring.readiness],
        data: this.mapAssessmentToSchemaAssessment(assessment, scoring),
      }));
  }

  @Watch("selectedAssessmentIds")
  public onSelectedAssessmentIdsChange(newValue: string[], oldValue: string[]) {
    this.allAssessmentsSelected = newValue.length === this.assessments.length;
    this.setComparableAssessments();

    if (newValue.length < oldValue.length) {
      const removedAssessmentIds = oldValue.filter((x) => !newValue.includes(x));
      for (const removedAssessmentId of removedAssessmentIds) {
        for (const preset of this.comparisonPresets) {
          preset.items = preset.items.filter((x) => x.id !== removedAssessmentId);
        }

        const presetToRemove = this.comparisonPresets.find((x) => x.id === `insight-preset-${removedAssessmentId}`);
        if (presetToRemove) {
          this.removePreset(presetToRemove);
        }
      }
    }

    if (this.userIsGlobalAdmin) {
      if (newValue.length > oldValue.length && !this.settingInitialValues) {
        const addedAssessmentIds = newValue.filter((x) => !oldValue.includes(x));
        for (const addedAssessmentId of addedAssessmentIds) {
          if (!this.comparisonPresets.find((x) => x.id === `insight-preset-${addedAssessmentId}`)) {
            this.getExecutiveSummary(addedAssessmentId).then((summary) => {
              this.$set(this.executiveSummaries, addedAssessmentId, summary);

              this.createPresetForNewAssessment(addedAssessmentId, summary);
            });
          }
        }
      }
    }
  }

  async getExecutiveSummary(assessmentId: string) {
    if (this.executiveSummaries[assessmentId]) {
      return this.executiveSummaries[assessmentId];
    }

    const response = await systemAPI.getAssessmentInsight(assessmentId);
    const insight = response.data.insight;
    if (insight && typeof insight === "object") {
      return Object.entries(insight)
        .map(([key, value]) => `• ${key}: ${value}`)
        .join(" ");
    }
    return insight;
  }

  public createPresetForNewAssessment(assessmentId: string, executiveSummary: string) {
    const assessment = this.compariableAssessments.find((x) => x.id === assessmentId);
    if (!assessment) return;

    const benchmark = this.compariableBenchmarks.find((x) => x.id === this.rootBenchmarkId);
    if (!benchmark) return;

    const preset: ComparisonChartPreset = {
      id: `insight-preset-${assessment.id}`,
      name: assessment.data.candidateName,
      description: executiveSummary,
      items: [assessment, benchmark],
    };

    this.comparisonPresets.push(preset);
  }

  mounted() {
    const defaultTraitWeights = TRAITS.reduce((acc, trait) => {
      acc[trait] = 0;
      return acc;
    }, {} as { [trait: string]: number });
    if (this.initialReport) {
      if (this.initialReport.type !== this.reportType) {
        throw new Error("Only team-effectiveness reports are supported");
      }
      this.settingInitialValues = true;

      this.editMode = true;
      this.reportName = this.initialReport.name;
      this.reportDescription = this.initialReport.description;
      this.reportVersion = this.initialReport.version;
      this.selectedAssessmentIds = this.initialReport.assessmentIds;
      this.traitPriorities = this.initialReport.data.traitPriorities;
      this.traitWeights = this.initialReport.data.traitWeights || defaultTraitWeights;
      this.tags = this.initialReport.data.tags || {};
      this.tagColours = this.initialReport.data.tagColours || {};
      this.reportOverviewCustomText = this.initialReport.data.reportOverviewCustomText;
      this.teamAlignmentMustHaveCustomText = this.initialReport.data.teamAlignmentMustHaveCustomText;
      this.teamAlignmentAllCustomText = this.initialReport.data.teamAlignmentAllCustomText;
      this.teamMatrixMustHaveCustomText = this.initialReport.data.teamMatrixMustHaveCustomText;
      this.teamMatrixTopPriorityCustomText = this.initialReport.data.teamMatrixTopPriorityCustomText;
      this.comparisonPresets = this.initialReport.data.comparisonPresets;
      this.executiveSummaries = this.initialReport.data.executiveSummaries || {};
      this.demoMode = this.initialReport.data.demoMode || false;

      // Use nextTick to ensure all reactive updates are complete
      this.$nextTick(() => {
        this.settingInitialValues = false;
      });
    } else {
      this.reportName = `${this.project.name} Team Effectiveness Report`;
      this.traitPriorities = this.traits.reduce((acc, trait) => {
        acc[trait] = 0;
        return acc;
      }, {} as { [trait: string]: number });
      this.traitWeights = defaultTraitWeights;
    }

    this.compariableBenchmarks = this.benchmarks.map((benchmark) => ({
      id: benchmark.identifier,
      name: benchmark.name,
      hidden: false,
      isLocked: false,
      selected: benchmark.default || false,
      color: benchmark.colour,
      initialColor: benchmark.colour,
      data: this.mapBenchmarkToSchemaBenchmark(benchmark),
    }));
    this.setComparableAssessments();
  }

  public colourDialogComplete(color: string): void {
    this.showColourPickerModal = false;
    if (this.selectedColourItem) {
      Vue.set(this.tagColours, this.selectedColourItem, color);
      Vue.nextTick(() => {
        this.tagColours = { ...this.tagColours }; // Force reactivity
      });
    }
  }

  public colourDialogClose(): void {
    this.showColourPickerModal = false;
  }

  public colourDialogReset(): void {
    if (this.selectedColourItem) {
      Vue.delete(this.tagColours, this.selectedColourItem);
    }

    this.showColourPickerModal = false;
  }

  public showColourPicker(item: string): void {
    this.selectedColourItem = item;
    this.showColourPickerModal = true;
  }
}
